import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import Navbar from '../components/Navbar';
import FeedbackThread from './FeedbackThread';
import './Feedback.css';

const Feedback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const threadId = searchParams.get('thread');
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [games, setGames] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNavigatingToThread, setIsNavigatingToThread] = useState(false);

  // New post form state
  const [newPost, setNewPost] = useState({
    type: 'game_issue',
    title: '',
    content: '',
    game_id: '',
    game_name: '',
    attachment: null,
    images: []
  });

  // Feedback types for creating new posts (system_notice only available in Manage page)
  const createFeedbackTypes = {
    game_issue: '游戏问题',
    info_error: '信息错误',
    manual_request: '手册请求/提交',
    new_game: '新游戏请求',
    site_bug: '网站Bug'
  };

  // All feedback types for display (includes system_notice for viewing)
  const feedbackTypes = {
    ...createFeedbackTypes,
    system_notice: '系统公告'
  };

  const statusTypes = {
    open: '待处理',
    replied: '已回复',
    completed: '已完成',
    delayed: '进行中',
    rejected: '暂不处理'
  };

  // eslint-disable-next-line no-unused-vars
  const statusColors = {
    open: '#e4cb86',
    replied: '#2196f3',
    completed: '#4caf50',
    delayed: '#ff9800',
    rejected: '#f44336'
  };

  useEffect(() => {
    // Reset navigation loading state when returning to main feedback page
    if (!threadId) {
      setIsNavigatingToThread(false);
    }
    
    fetchThreads();
    
    // Check if modal should be opened from URL params
    const openModal = searchParams.get('new');
    const gameId = searchParams.get('gameId');
    const gameName = searchParams.get('gameName');
    const type = searchParams.get('type');
    
    if (openModal === 'true') {
      setShowNewPostModal(true);
      // Fetch games when modal is opened
      if (games.length === 0) {
        fetchGames();
      }
      // Set type if provided
      if (type) {
        setNewPost(prev => ({
          ...prev,
          type: type
        }));
      }
      // Set game info if provided
      if (gameId && gameName) {
        setNewPost(prev => ({
          ...prev,
          game_id: gameId,
          game_name: gameName
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, filterType, filterStatus, currentPage, threadId]);

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 20,
        exclude_recondb: true  // Exclude recondb entries from feedback page
      };
      
      if (filterType !== 'all') params.type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      
      const response = await apiClient.get('/feedback/threads', { params });
      setThreads(response.data.threads);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching threads:', error);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, currentPage]);

  const fetchGames = async () => {
    try {
      const response = await apiClient.get('/games');
      setGames(response.data);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const handleNewPostSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    const formData = new FormData();
    formData.append('type', newPost.type);
    formData.append('title', newPost.title);
    formData.append('content', newPost.content);
    
    // All posts from public page are marked as visitor posts
    formData.append('created_by_developer', '0');
    
    if (newPost.game_id) {
      formData.append('game_id', newPost.game_id);
      formData.append('game_name', newPost.game_name);
    }
    
    // Handle manual_request PDF attachment (single file)
    if (newPost.type === 'manual_request' && newPost.attachment) {
      formData.append('attachment', newPost.attachment);
    }
    
    // Handle non-manual_request images (multiple)
    if (newPost.type !== 'manual_request' && newPost.images.length > 0) {
      newPost.images.forEach((image, index) => {
        formData.append(`image_${index}`, image);
      });
    }

    try {
      const response = await apiClient.post('/feedback/threads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Show success with link
      const threadId = response.data.threadId;
      const threadLink = `${window.location.origin}/feedback?thread=${threadId}`;
      
      // Show success modal
      setSubmissionResult({
        success: true,
        link: threadLink,
        threadId
      });
      
      // Reset form
      setNewPost({
        type: 'game_issue',
        title: '',
        content: '',
        game_id: '',
        game_name: '',
        attachment: null,
        images: []
      });
      
      // Refresh threads
      fetchThreads();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmissionResult({
        success: false,
        error: '提交失败，请稍后重试'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGameSelect = (e) => {
    const gameId = e.target.value;
    const selectedGame = games.find(g => g.id === parseInt(gameId));
    
    let gameName = '';
    if (selectedGame) {
      if (selectedGame.titlechn && selectedGame.titleeng) {
        gameName = `${selectedGame.titlechn} (${selectedGame.titleeng})`;
      } else {
        gameName = selectedGame.titlechn || selectedGame.titleeng || `Game ${selectedGame.id}`;
      }
    }
    
    setNewPost(prev => ({
      ...prev,
      game_id: gameId,
      game_name: gameName
    }));
  };

  // Helper function to get game display name - optimized to use thread.game_name directly
  const getGameDisplayName = (thread) => {
    // Use the game_name stored in the thread for display (already formatted)
    return thread.game_name || 'Unknown Game';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `${minutes}分钟前`;
    if (hours < 24) return `${hours}小时前`;
    if (days < 7) return `${days}天前`;
    
    return date.toLocaleDateString('zh-CN');
  };

  const handleThreadClick = (threadId) => {
    setIsNavigatingToThread(true);
    // Small delay to show loading state before navigation
    setTimeout(() => {
      navigate(`/feedback?thread=${threadId}`);
    }, 100);
  };

  const requiresGame = ['game_issue', 'info_error', 'manual_request'].includes(newPost.type);

  // Handle image upload for non-recondb submissions
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = 3 - newPost.images.length;
    const filesToAdd = files.slice(0, remainingSlots);
    
    // Validate file types (only images)
    const validImages = filesToAdd.filter(file => file.type.startsWith('image/'));
    
    if (validImages.length < filesToAdd.length) {
      alert('只能上传图片文件');
    }
    
    setNewPost(prev => ({
      ...prev,
      images: [...prev.images, ...validImages]
    }));
  };

  const removeImage = (index) => {
    setNewPost(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleCopyLink = () => {
    if (submissionResult && submissionResult.link) {
      navigator.clipboard.writeText(submissionResult.link);
      // Show temporary "copied" feedback
      const button = document.querySelector('.feedback-copy-link-button');
      if (button) {
        const originalText = button.textContent;
        button.textContent = '链接已复制！';
        setTimeout(() => {
          button.textContent = originalText;
        }, 2000);
      }
    }
  };

  const handleCloseSuccessModal = () => {
    setSubmissionResult(null);
    setShowNewPostModal(false);
    if (submissionResult && submissionResult.threadId) {
      navigate(`/feedback?thread=${submissionResult.threadId}`);
    }
  };

  // If threadId is present, show thread view
  if (threadId) {
    return <FeedbackThread />;
  }

  return (
    <div className="feedback-page">
      <Navbar />
      
      <div className="feedback-container">
        <div className="feedback-controls">
          <div className="feedback-filters">
            <div className="filter-group">
              <label>类型：</label>
              <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}>
                <option value="all">全部</option>
                {Object.entries(feedbackTypes).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>状态：</label>
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
                <option value="all">全部</option>
                {Object.entries(statusTypes).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <button 
            className="new-post-button"
            onClick={() => {
              setShowNewPostModal(true);
              // Lazy load games only when modal is opened
              if (games.length === 0) {
                fetchGames();
              }
            }}
          >
            <span className="plus-icon">+</span>
            新建反馈
          </button>
        </div>

        {loading || isNavigatingToThread ? (
          <div className="feedback-loading">
            {isNavigatingToThread ? '正在打开反馈...' : '加载中...'}
          </div>
        ) : (
          <>
            <div className="feedback-threads">
              {threads.length === 0 ? (
                <div className="no-threads">暂无反馈</div>
              ) : (
                threads
                  .filter(thread => thread.type !== 'recondb_entry')
                  .map(thread => (
                  <div 
                    key={thread.id} 
                    className={`thread-card ${thread.is_pinned === 1 ? 'pinned' : ''}`}
                    onClick={() => handleThreadClick(thread.id)}
                  >
                    <div className="thread-content">
                      <div className="thread-header">
                        <span className="thread-type">{feedbackTypes[thread.type]}</span>
                        <div className={`thread-status-flag status-${thread.status}`}>
                          <span className="status-text">{statusTypes[thread.status]}</span>
                        </div>
                      </div>
                      
                      <h3 className="thread-title">{thread.title}</h3>
                      
                      {(thread.game_id || thread.game_name) && (
                        <div className="thread-game">关联游戏: {getGameDisplayName(thread)}</div>
                      )}
                      
                      <div className="thread-footer">
                        <span className="thread-date">{formatDate(thread.created_at)}</span>
                        <span className="thread-comments">{thread.comment_count} 条回复</span>
                        <div className="thread-votes">
                          <span className="vote-count upvote">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                            </svg>
                            {thread.upvotes || 0}
                          </span>
                          <span className="vote-count downvote">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
                            </svg>
                            {thread.downvotes || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {totalPages > 1 && (
              <div className="feedback-pagination">
                {totalPages <= 10 ? (
                  // Tab-style pagination for 10 or fewer pages
                  <div className="pagination-tabs">
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        className={`page-tab ${currentPage === index + 1 ? 'active' : ''}`}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                ) : (
                  // Traditional pagination for more than 10 pages
                  <div className="pagination-traditional">
                    <button 
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                      上一页
                    </button>
                    <span className="page-info">第 {currentPage} / {totalPages} 页</span>
                    <button 
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                      下一页
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* New Post Modal */}
      {showNewPostModal && (
        <div className="feedback-modal-overlay" onClick={() => !submissionResult && setShowNewPostModal(false)}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            {!submissionResult ? (
              <>
                <div className="modal-header">
                  <h2>新建反馈</h2>
                  <button className="modal-close" onClick={() => setShowNewPostModal(false)}>×</button>
                </div>

                <form onSubmit={handleNewPostSubmit} className="feedback-form">
              <div className="form-group">
                <label>反馈类型 *</label>
                <select 
                  value={newPost.type} 
                  onChange={(e) => setNewPost({ ...newPost, type: e.target.value })}
                  required
                >
                  {Object.entries(createFeedbackTypes).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              {requiresGame && (
                <div className="form-group">
                  <label>关联游戏 *</label>
                  <select 
                    value={newPost.game_id} 
                    onChange={handleGameSelect}
                    required
                  >
                    <option value="">请选择游戏</option>
                    {games.map(game => (
                      <option key={game.id} value={game.id}>
                        {game.titlechn && game.titleeng 
                          ? `${game.titlechn} (${game.titleeng})`
                          : game.titlechn || game.titleeng || `Game ${game.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>标题 *</label>
                <input 
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  placeholder="简短描述问题"
                  required
                />
              </div>

              <div className="form-group">
                <label>详细描述 *</label>
                <textarea 
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  placeholder="请详细描述您的问题或建议..."
                  rows="6"
                  required
                />
              </div>

              {/* Different attachment UI for manual_request vs others */}
              {newPost.type === 'manual_request' ? (
                <div className="form-group">
                  <label>提交手册扫描（可选）</label>
                  <div className="pdf-upload-wrapper">
                    <label className="pdf-upload-button">
                      <input 
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // Check file size (30MB = 30 * 1024 * 1024 bytes)
                            if (file.size > 30 * 1024 * 1024) {
                              alert('文件大小不能超过30MB');
                              e.target.value = '';
                              return;
                            }
                            // Check file type
                            if (file.type !== 'application/pdf') {
                              alert('只能上传PDF文件');
                              e.target.value = '';
                              return;
                            }
                            setNewPost({ ...newPost, attachment: file });
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                      <span className="button-text">
                        {newPost.attachment ? newPost.attachment.name : '选择文件'}
                      </span>
                    </label>
                    {newPost.attachment && (
                      <button 
                        type="button" 
                        className="pdf-remove-btn"
                        onClick={() => setNewPost({ ...newPost, attachment: null })}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <small className="form-hint">
                    仅支持PDF格式，最大30MB
                  </small>
                </div>
              ) : (
                <div className="form-group">
                  <label>图片（可选）</label>
                  <div className="image-upload-container">
                    {newPost.images.map((image, index) => (
                      <div key={index} className="image-preview">
                        <img src={URL.createObjectURL(image)} alt={`预览 ${index + 1}`} />
                        <button 
                          type="button" 
                          className="remove-image-btn"
                          onClick={() => removeImage(index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    
                    {newPost.images.length < 3 && (
                      <label className="image-upload-box">
                        <input 
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                        />
                        <div className="upload-placeholder">
                          <span className="upload-icon">+</span>
                          <span className="upload-text">添加图片</span>
                        </div>
                      </label>
                    )}
                  </div>
                  <small className="form-hint">
                    最多上传3张图片，支持JPG、PNG、GIF等格式
                  </small>
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowNewPostModal(false)} disabled={isSubmitting}>
                  取消
                </button>
                <button type="submit" className="btn-submit" disabled={isSubmitting}>
                  {isSubmitting ? '提交中...' : '提交反馈'}
                </button>
              </div>
            </form>
              </>
            ) : (
              <div className="feedback-success-container">
                {submissionResult.success ? (
                  <>
                    <div className="feedback-success-icon">
                      <svg viewBox="0 0 100 100" className="success-svg">
                        <circle className="success-circle" cx="50" cy="50" r="45" />
                        <path className="success-check" d="M25 50 L40 65 L75 30" />
                      </svg>
                    </div>
                    <h2>感谢您的反馈！</h2>
                    <p className="feedback-success-message">您的反馈已提交成功。您可以通过以下链接查看和追踪：</p>
                    <div className="feedback-link-container">
                      <input 
                        type="text" 
                        value={submissionResult.link} 
                        readOnly 
                        className="feedback-link-input"
                      />
                      <button 
                        className="feedback-copy-link-button"
                        onClick={handleCopyLink}
                      >
                        复制链接
                      </button>
                    </div>
                    <div className="feedback-success-actions">
                      <button 
                        className="feedback-btn-view"
                        onClick={handleCloseSuccessModal}
                      >
                        查看反馈
                      </button>
                      <button 
                        className="feedback-btn-close-only"
                        onClick={() => {
                          setSubmissionResult(null);
                          setShowNewPostModal(false);
                        }}
                      >
                        关闭
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="feedback-error-icon">✕</div>
                    <h2>{submissionResult.error}</h2>
                    <button 
                      className="feedback-btn-close"
                      onClick={() => setSubmissionResult(null)}
                    >
                      关闭
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback;
